/**
 * @fileoverview Notificador de insights automáticos do pipeline.
 *
 * Monitora dados do pipeline e gera notificações quando detecta:
 * - Gargalos: etapas com tempo médio acima de 3 dias
 * - Melhor canal: origem com maior taxa de conversão
 * - Conversão baixa: menos de 30% das propostas virando fechamento
 *
 * Envia toasts in-app, notificações push do navegador e reproduz som.
 * Apenas notifica insights NOVOS (compara IDs com a execução anterior).
 */
import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useStore } from '@/store/useStore';
import { useDealsList } from '@/hooks/useDealsList';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { playPlim } from '@/lib/sounds';
import { buildStageSemantics } from '@/lib/stageSemantics';

interface Insight {
  id: string;
  title: string;
  description: string;
  type: 'warning' | 'insight' | 'alert' | 'opportunity' | 'info';
}

function buildInsights(deals: any[], kanbanColumns: any[]): Insight[] {
  // Ids ESTÁVEIS (sem números): o notificador avisa quando um insight
  // aparece/muda de estágio, não a cada lead movido que mexe numa média.
  // Antes o id levava `avgDays`/`convRate` — com a store viva, cada
  // movimento virava toast + som + push "novo".
  const stages = buildStageSemantics(kanbanColumns);
  const openDeals = deals.filter((d: any) => !stages.isClosed(d.column));
  const items: Insight[] = [];

  // Stage data — só estágios abertos: "Perdido" com 800 leads parados não é gargalo.
  const stageData = stages.columns
    .filter(col => !stages.isClosed(col.id))
    .map(col => {
      const inStage = openDeals.filter((d: any) => d.column === col.id);
      return {
        id: col.id,
        label: col.label,
        count: inStage.length,
        avgDays: Math.round(inStage.reduce((s: number, d: any) => s + (d.daysInColumn || 0), 0) / (inStage.length || 1)),
      };
    });

  // Bottleneck
  if (stageData.length > 0) {
    const bottleneck = stageData.reduce((max, s) => s.avgDays > max.avgDays ? s : max, stageData[0]);
    if (bottleneck.avgDays > 3 && bottleneck.count > 0) {
      items.push({
        id: `bottleneck-${bottleneck.id}`,
        title: 'Gargalo Detectado',
        description: `Leads ficam ${bottleneck.avgDays}d em "${bottleneck.label}" (${bottleneck.count} leads)`,
        type: 'warning',
      });
    }
  }

  // Best source — skip leads with empty/missing origin so a noisy "" bucket
  // doesn't crowd out the real top channel.
  const sources = new Map<string, { leads: number; closed: number }>();
  deals.forEach((d: any) => {
    const origin = (d.origin || '').toString().trim();
    if (!origin) return;
    const s = sources.get(origin) || { leads: 0, closed: 0 };
    s.leads++;
    if (stages.isWon(d.column)) s.closed++;
    sources.set(origin, s);
  });
  const bestSource = Array.from(sources.entries())
    // Avoid one-off origins masquerading as the "best channel" — require
    // at least 3 leads before counting a channel as comparable.
    .filter(([, data]) => data.leads >= 3)
    .map(([name, data]) => ({ name, ...data, convRate: data.leads > 0 ? Math.round((data.closed / data.leads) * 100) : 0 }))
    .sort((a, b) => b.convRate - a.convRate)[0];
  if (bestSource && bestSource.convRate > 0) {
    items.push({
      id: `best-source-${bestSource.name}`,
      title: 'Melhor Canal',
      description: `${bestSource.name}: ${bestSource.convRate}% de conversão`,
      type: 'insight',
    });
  }

  // Low conversion: propostas (proposta → ganho) que viraram ganho.
  const propostaDeals = deals.filter((d: any) => stages.reachedProposal(d.column) || stages.isWon(d.column));
  const fechamentoDeals = deals.filter((d: any) => stages.isWon(d.column));
  const convRate = propostaDeals.length > 0 ? Math.round((fechamentoDeals.length / propostaDeals.length) * 100) : 0;
  if (convRate < 30 && propostaDeals.length > 0) {
    items.push({
      id: 'low-conv',
      title: 'Conversão Baixa',
      description: `Apenas ${convRate}% das propostas viram fechamento`,
      type: 'alert',
    });
  }

  return items;
}

function sendBrowserNotification(title: string, body: string, tag: string) {
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  try {
    new Notification(title, {
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      // Per-insight tag so simultaneous insights stack instead of replacing
      // each other — the previous shared 'insight-notification' tag meant
      // each new insight overwrote the prior one.
      tag,
    });
  } catch {
    // Fallback for environments that don't support Notification constructor
  }
}

/**
 * Monitors pipeline data for new insights and sends push + in-app notifications.
 * Place inside AppLayout alongside DbSync.
 */
export default function InsightNotifier() {
  const { deals, isLoading: dealsLoading } = useDealsList();
  const activeProjectId = useStore(s => s.activeProjectId);
  const kanbanColumns = useStore(s => s.kanbanColumns);
  const navigate = useNavigate();
  const location = useLocation();
  const prevInsightIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);
  const permissionRequestedRef = useRef(false);

  /**
   * Lazy permission request — defer until we actually have an insight
   * worth pushing. Asking for browser-notification permission immediately
   * on first visit gets the prompt dismissed almost universally; waiting
   * until the user has data to be notified about lands far better.
   * Idempotent via `permissionRequestedRef` so we never re-prompt within
   * a session (the browser also enforces this, but this avoids the wasted
   * call entirely).
   */
  const ensureNotificationPermission = useCallback(() => {
    if (permissionRequestedRef.current) return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    permissionRequestedRef.current = true;
    Notification.requestPermission().catch(() => { /* user dismissed */ });
  }, []);

  const currentInsights = useMemo(() => buildInsights(deals, kanbanColumns), [deals, kanbanColumns]);

  // Troca de projeto: os insights do projeto novo não são "novidade" — re-semeia.
  useEffect(() => {
    initializedRef.current = false;
    prevInsightIdsRef.current = new Set();
  }, [activeProjectId]);

  useEffect(() => {
    // Só semeia depois da primeira carga real. Antes, o primeiro run via a
    // lista vazia e, quando os leads chegavam, TODOS os insights viravam
    // "novos" — rajada de toasts + som + pedido de permissão a cada abertura.
    if (dealsLoading) return;
    const currentIds = new Set(currentInsights.map(i => i.id));

    if (!initializedRef.current) {
      // First run: seed the known insights, don't notify
      prevInsightIdsRef.current = currentIds;
      initializedRef.current = true;
      return;
    }

    // Find new insights that weren't in the previous set
    const newInsights = currentInsights.filter(i => !prevInsightIdsRef.current.has(i.id));
    if (newInsights.length === 0) {
      prevInsightIdsRef.current = currentIds;
      return;
    }

    // We have something to notify about — ask for browser permission now.
    ensureNotificationPermission();

    // Skip the redundant CTA when the user is already on the Highlights page.
    const onHighlights = location.pathname.startsWith('/highlights');

    newInsights.forEach(insight => {
      // In-app toast + sound
      const toastType = insight.type === 'warning' ? toast.warning
        : insight.type === 'alert' ? toast.error
        : insight.type === 'insight' ? toast.success
        : toast.info;

      toastType(insight.title, {
        description: insight.description,
        duration: 8000,
        action: onHighlights ? undefined : {
          label: 'Ver Highlights',
          onClick: () => navigate('/highlights'),
        },
      });

      // Play plim notification sound
      try { playPlim(0.4); } catch { /* empty */ }

      // Browser push notification — per-insight tag so multiple stack
      // instead of replacing each other.
      sendBrowserNotification(`📊 ${insight.title}`, insight.description, `insight:${insight.id}`);
    });

    prevInsightIdsRef.current = currentIds;
  }, [currentInsights, dealsLoading, ensureNotificationPermission, location.pathname, navigate]);

  return null;
}
